import PolicyLayout, { Section } from "@/components/PolicyLayout";
import { BUSINESS, addressOneLine } from "@/data/business";
import { Mail, Phone, MapPin, Heart, ShieldCheck, Sparkles } from "lucide-react";

export default function About() {
  return (
    <PolicyLayout
      eyebrow="Our story"
      title="About Pawventure Essentials"
      intro="A small, dog-obsessed team building toys that actually survive your dog — and your living room."
      testId="about-page"
    >
      <Section title="Why we exist">
        <p>
          Pawventure Essentials started in {BUSINESS.establishedYear} after one too many tennis
          balls disintegrated within a week of being unboxed. We wanted a toy that could keep up
          with real dogs — the heavy chewers, the obsessive fetchers, the pool-loving labs, and the
          terriers who think they're labs.
        </p>
        <p>
          Our flagship is the squeaky rugby ball: anti-bite TPR, satisfying squeaker, unpredictable
          bounce. We tested it across breeds from teacup pups to retrievers before bringing it to
          market. The result is a single, simple product that's built to last.
        </p>
      </Section>

      <Section title="What we stand for">
        <ul className="list-none space-y-4">
          <li className="flex gap-4">
            <ShieldCheck className="text-primary shrink-0 mt-1" size={22} strokeWidth={2.5} />
            <div>
              <strong className="text-foreground font-display">Quality without the markup.</strong>{" "}
              We sell direct, skip the middle-men, and pass the savings on. No subscription traps,
              no upsell carousel.
            </div>
          </li>
          <li className="flex gap-4">
            <Heart className="text-primary shrink-0 mt-1" size={22} strokeWidth={2.5} />
            <div>
              <strong className="text-foreground font-display">Built around real play.</strong>{" "}
              Our designs come from watching dogs play — not focus groups. If our team's dogs won't
              touch a prototype, we throw it out.
            </div>
          </li>
          <li className="flex gap-4">
            <Sparkles className="text-primary shrink-0 mt-1" size={22} strokeWidth={2.5} />
            <div>
              <strong className="text-foreground font-display">Plain-English everything.</strong>{" "}
              No legalese, no buried fees, no hidden subscriptions. The price you see is the price
              you pay.
            </div>
          </li>
        </ul>
      </Section>

      <Section title="Our promise">
        <p>
          Every Pawventure order is backed by our 30-day Tail-Wag Guarantee. If your dog isn't
          obsessed, we'll refund or replace — no awkward questions. See our{" "}
          <a href="/return-policy" className="text-primary font-semibold">Return Policy</a> for details.
        </p>
      </Section>

      <Section title="Get in touch">
        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          <div className="rounded-2xl border border-border p-5 flex gap-3">
            <Mail className="text-primary shrink-0 mt-0.5" size={20} strokeWidth={2.5} />
            <div className="text-sm leading-relaxed">
              <p className="font-semibold text-foreground">Email</p>
              <p>
                <a href={`mailto:${BUSINESS.email.support}`} className="text-primary font-semibold">{BUSINESS.email.support}</a>
              </p>
            </div>
          </div>
          <div className="rounded-2xl border border-border p-5 flex gap-3">
            <Phone className="text-primary shrink-0 mt-0.5" size={20} strokeWidth={2.5} />
            <div className="text-sm leading-relaxed">
              <p className="font-semibold text-foreground">Phone</p>
              <p>{BUSINESS.phone}</p>
            </div>
          </div>
          <div className="rounded-2xl border border-border p-5 flex gap-3 sm:col-span-2">
            <MapPin className="text-primary shrink-0 mt-0.5" size={20} strokeWidth={2.5} />
            <div className="text-sm leading-relaxed">
              <p className="font-semibold text-foreground">Mailing address</p>
              <p>{addressOneLine()}</p>
            </div>
          </div>
        </div>
        <p className="text-sm mt-4 text-muted-foreground">
          We respond to support emails within one business day, usually faster.
        </p>
      </Section>
    </PolicyLayout>
  );
}
